import { Body, Button, Flex } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint/chakra';
import React, { useContext, useMemo, useState } from 'react';

import { ImageWithTint } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ImageWithTint';
import { TestLabels } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { labelNames } from 'src/modules/designStudio/constants/productCatalog';
import type { TextureAppearance } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  ColorGroupNames,
  categorizeColor,
} from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import { productCatalogContext } from 'src/modules/designStudio/providers';
import type { ProductEntry } from 'src/modules/designStudio/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export interface GroupCardParams extends FlexProps {
  groupAppearance: TextureAppearance;
  onSelected: () => void;
}

export const GroupCard = ({ groupAppearance, onSelected }: GroupCardParams) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { textures } = useContext(productCatalogContext);

  const texture: ProductEntry = useMemo(
    () =>
      textures?.find(
        tex => tex.id === groupAppearance.textureId,
      ) as ProductEntry,
    [textures, groupAppearance],
  );

  const materialName = useMemo(() => {
    const label = texture?.label_id;
    return label ? labelNames[label] : 'Wall';
  }, [texture]);

  const colorName = useMemo(
    () => ColorGroupNames[categorizeColor(groupAppearance.color ?? '#ffffff')],
    [groupAppearance],
  );

  const thumbnail = useMemo(() => texture?.texture?.thumbnail.url, [texture]);

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Flex
      _hover={{
        outlineStyle: 'solid',
        outlineThickness: '2px',
        outlineColor: 'neutral.300',
        bg: 'white',
      }}
      bg="neutral.100"
      borderRadius={450}
      direction="column"
      gap={0}
      maxWidth="156px"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      width="100%"
    >
      <ImageWithTint
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        borderTopLeftRadius={450}
        borderTopRightRadius={450}
        color={groupAppearance.color ?? '#ffffff'}
        height="110px"
        thumbnail={thumbnail ?? ''}
      />
      <Flex
        alignItems="start"
        bg="clear"
        borderBottomLeftRadius={450}
        borderBottomRightRadius={450}
        direction="column"
        gap={0}
        p="16px"
      >
        <Body fontSize="15px" fontWeight={700} lineHeight="18px" my={0}>
          {materialName}
        </Body>
        <Body
          color="neutral.600"
          fontSize="15px"
          fontWeight={400}
          lineHeight="18.75px"
          my={0}
        >
          {colorName}
        </Body>

        <Button
          aria-label={TestLabels.WALL_GROUP_EDIT}
          colorScheme={isHovered ? 'dark' : 'light'}
          marginTop="8px"
          onClick={onSelected}
          size="small"
          variant={isHovered ? 'secondary' : 'primary'}
          width="100%"
        >
          {t('productCatalogMenu.productDetails.edit')}
        </Button>
      </Flex>
    </Flex>
  );
};
